/**
 * Filtering people by the enterprise they belong to — or by belonging to none.
 *
 * Shared by the Codeurs and Superviseurs pages, which offer the same filter over the
 * same relation. The reserved value and the translation into API parameters live
 * together: two copies of a magic string that has to agree with the request it produces
 * would eventually drift, and the symptom would be a filter that silently returns
 * everything.
 */

/**
 * Reserved value of the `enterprise` filter meaning "belonging to none".
 *
 * Kept in the same field as a real enterprise name so the existing dropdown, reset button
 * and `hasActiveFilters` keep working untouched, and so the filter stays visible in the
 * bar rather than hidden.
 */
export const WITHOUT_ENTERPRISE = "__sans_entreprise__";

/** Label shown for it, in the dropdown and as the selected value. */
export const WITHOUT_ENTERPRISE_LABEL = "Sans entreprise";

/**
 * Adds the enterprise filter to a request.
 *
 * The reserved value becomes the API's own `withoutEnterprise` flag — a separate
 * parameter precisely because `enterprise` matches a name and a name could collide with
 * any sentinel. Anything else is a name to match.
 */
export const appendEnterpriseFilter = (
  params: URLSearchParams,
  enterprise: string,
): void => {
  if (enterprise === WITHOUT_ENTERPRISE) {
    params.append("withoutEnterprise", "true");
  } else if (enterprise) {
    params.append("enterprise", enterprise);
  }
};

/** The option list for an enterprise dropdown: all, none, then the names. */
export const enterpriseFilterOptions = (
  names: string[],
  allLabel: string,
): Array<{ value: string; label: string }> => [
  { value: "", label: allLabel },
  { value: WITHOUT_ENTERPRISE, label: WITHOUT_ENTERPRISE_LABEL },
  ...names.map((name) => ({ value: name, label: name })),
];

/** Label for whatever the filter currently holds. */
export const enterpriseFilterLabel = (enterprise: string): string =>
  enterprise === WITHOUT_ENTERPRISE ? WITHOUT_ENTERPRISE_LABEL : enterprise;
